import { createContext, useContext, useCallback, useRef, useSyncExternalStore } from 'react';

/**
 * Global in-memory store for long-running task state that must survive
 * React Router page navigation.  Each task is identified by a string key
 * (e.g. "onpage-scan", "ai-suggestions", "competitor-content-gap").
 *
 * Shape per task:
 *   { running: bool, phase: number, result: any, error: string|null, startedAt: number|null }
 */

const INITIAL_TASK = Object.freeze({ running: false, phase: 0, result: null, error: null, startedAt: null });

let store = {};
let listeners = new Set();

function emitChange() {
  listeners.forEach((l) => l());
}

function getSnapshot() {
  return store;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const TaskStateContext = createContext(null);

export function TaskStateProvider({ children }) {
  const storeRef = useRef(store);

  const getTask = useCallback((key) => {
    return store[key] || INITIAL_TASK;
  }, []);

  const setTask = useCallback((key, partial) => {
    const prev = store[key] || { ...INITIAL_TASK };
    store = { ...store, [key]: { ...prev, ...partial } };
    storeRef.current = store;
    emitChange();
  }, []);

  const startTask = useCallback((key) => {
    store = {
      ...store,
      [key]: { running: true, phase: 0, result: null, error: null, startedAt: Date.now() },
    };
    storeRef.current = store;
    emitChange();
  }, []);

  const advancePhase = useCallback((key, maxPhase = Infinity) => {
    const prev = store[key];
    if (!prev) return;
    const nextPhase = Math.min(prev.phase + 1, maxPhase);
    if (nextPhase === prev.phase) return;
    store = { ...store, [key]: { ...prev, phase: nextPhase } };
    storeRef.current = store;
    emitChange();
  }, []);

  const completeTask = useCallback((key, result) => {
    store = {
      ...store,
      [key]: { running: false, phase: 0, result, error: null, startedAt: null },
    };
    storeRef.current = store;
    emitChange();
  }, []);

  const failTask = useCallback((key, error) => {
    store = {
      ...store,
      [key]: { running: false, phase: 0, result: null, error: String(error), startedAt: null },
    };
    storeRef.current = store;
    emitChange();
  }, []);

  const clearTask = useCallback((key) => {
    const { [key]: _, ...rest } = store;
    store = rest;
    storeRef.current = store;
    emitChange();
  }, []);

  const value = { getTask, setTask, startTask, advancePhase, completeTask, failTask, clearTask };

  return (
    <TaskStateContext.Provider value={value}>
      {children}
    </TaskStateContext.Provider>
  );
}

/**
 * Hook to subscribe to a specific task key reactively.
 * Re-renders only when that task's state changes.
 */
export function useTaskState(key) {
  const ctx = useContext(TaskStateContext);
  if (!ctx) throw new Error('useTaskState must be used within TaskStateProvider');

  const snap = useSyncExternalStore(subscribe, () => store[key] || INITIAL_TASK);

  return {
    ...snap,
    start: () => ctx.startTask(key),
    advance: (maxPhase) => ctx.advancePhase(key, maxPhase),
    complete: (result) => ctx.completeTask(key, result),
    fail: (error) => ctx.failTask(key, error),
    clear: () => ctx.clearTask(key),
    set: (partial) => ctx.setTask(key, partial),
  };
}

/**
 * True if any of the given task keys is currently running (for nav indicators).
 */
export function useAnyTaskRunning(keys) {
  const ctx = useContext(TaskStateContext);
  if (!ctx) throw new Error('useAnyTaskRunning must be used within TaskStateProvider');

  return useSyncExternalStore(
    subscribe,
    () => keys.some((k) => (store[k] || INITIAL_TASK).running),
    () => false,
  );
}

export default TaskStateContext;
